// Utility to find optimal label point inside a polygon (array of lat/lon pairs)
// Exports function labelPoint(polygon, precision)

// polygon: Array of points. Each point [lat, lon] or {lat:..., lon:...} or {latitude, longitude}
// Returns {lat, lon, distance} where distance is the distance (in degrees) from the point to the polygon edge (>0 if inside)

function toXY(pt) {
    if (Array.isArray(pt)) {
        return ({x: pt[0], y: pt[1]});
    }
    // fallback assume {x,y}
    return {x: pt.x, y: pt.y};
}

// Signed distance from point to polygon (positive = inside, negative = outside)
function pointToPolygonDist(x, y, polygon) {
    let inside = false;
    let minDistSq = Infinity;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const pi = toXY(polygon[i]);
        const pj = toXY(polygon[j]);
        // Ray-casting for inside test
        if ((pi.y > y) !== (pj.y > y) &&
            x < (pj.x - pi.x) * (y - pi.y) / (pj.y - pi.y) + pi.x) {
            inside = !inside;
        }
        // distance to segment
        const distSq = pointSegmentDistSq(x, y, pi.x, pi.y, pj.x, pj.y);
        if (distSq < minDistSq) minDistSq = distSq;
    }
    const dist = Math.sqrt(minDistSq);
    return inside ? dist : -dist;
}

function pointSegmentDistSq(px, py, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
        const vx = px - x1;
        const vy = py - y1;
        return vx * vx + vy * vy;
    }
    const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
    if (t < 0) {
        const vx = px - x1;
        const vy = py - y1;
        return vx * vx + vy * vy;
    } else if (t > 1) {
        const vx = px - x2;
        const vy = py - y2;
        return vx * vx + vy * vy;
    }
    const projx = x1 + t * dx;
    const projy = y1 + t * dy;
    const vx = px - projx;
    const vy = py - projy;
    return vx * vx + vy * vy;
}

function centroid(polygon) {
    let area = 0, x = 0, y = 0;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const pi = toXY(polygon[i]);
        const pj = toXY(polygon[j]);
        const f = pj.x * pi.y - pi.x * pj.y;
        x += (pj.x + pi.x) * f;
        y += (pj.y + pi.y) * f;
        area += f;
    }
    if (area === 0) {
        // fallback to average
        let sx = 0, sy = 0;
        for (let p of polygon) {
            const q = toXY(p);
            sx += q.x;
            sy += q.y;
        }
        return {x: sx / polygon.length, y: sy / polygon.length};
    }
    area *= 0.5;
    const cx = x / (6 * area);
    const cy = y / (6 * area);
    return {x: cx, y: cy};
}

// Simple max-heap for cells
class MaxHeap {
    constructor() {
        this.items = [];
    }

    push(item) {
        this.items.push(item);
        this._siftUp(this.items.length - 1);
    }

    pop() {
        if (this.items.length === 0) return null;
        const top = this.items[0];
        const end = this.items.pop();
        if (this.items.length > 0) {
            this.items[0] = end;
            this._siftDown(0);
        }
        return top;
    }

    _siftUp(i) {
        const items = this.items;
        while (i > 0) {
            const p = Math.floor((i - 1) / 2);
            if (items[i].max <= items[p].max) break;
            [items[i], items[p]] = [items[p], items[i]];
            i = p;
        }
    }

    _siftDown(i) {
        const items = this.items;
        const n = items.length;
        while (true) {
            let left = 2 * i + 1, right = left + 1, largest = i;
            if (left < n && items[left].max > items[largest].max) largest = left;
            if (right < n && items[right].max > items[largest].max) largest = right;
            if (largest === i) break;
            [items[i], items[largest]] = [items[largest], items[i]];
            i = largest;
        }
    }

    get size() {
        return this.items.length;
    }
}

// The main function
function labelPoint(polygon, precision = 1e-6) {
    if (!polygon || polygon.length === 0) return null;
    // bounding box
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let p of polygon) {
        const q = toXY(p);
        if (q.x < minX) minX = q.x;
        if (q.y < minY) minY = q.y;
        if (q.x > maxX) maxX = q.x;
        if (q.y > maxY) maxY = q.y;
    }
    const width = maxX - minX;
    const height = maxY - minY;
    const cellSize = Math.min(width, height);
    const h = cellSize / 2;

    if (cellSize === 0) {
        // degenerate polygon
        const p = toXY(polygon[0]);
        return {lat: p.y, lon: p.x, distance: 0};
    }

    const heap = new MaxHeap();
    // initial grid
    for (let x = minX; x < maxX; x += cellSize) {
        for (let y = minY; y < maxY; y += cellSize) {
            const cx = x + h;
            const cy = y + h;
            const d = pointToPolygonDist(cx, cy, polygon);
            const cell = {x: cx, y: cy, h: h, d: d, max: d + h * Math.SQRT2};
            heap.push(cell);
        }
    }

    // take centroid as initial best
    let bestCell;
    const c = centroid(polygon);
    const cDist = pointToPolygonDist(c.x, c.y, polygon);
    if (cDist > 0) {
        bestCell = {x: c.x, y: c.y, d: cDist};
    } else {
        // try bbox center
        const bboxCenter = {x: minX + width / 2, y: minY + height / 2};
        const bDist = pointToPolygonDist(bboxCenter.x, bboxCenter.y, polygon);
        bestCell = {x: bboxCenter.x, y: bboxCenter.y, d: bDist};
    }

    // Also check initial grid for a better cell
    // (heap contains grid cells)
    while (heap.size) {
        const cell = heap.pop();
        if (cell.d > bestCell.d) {
            bestCell = {x: cell.x, y: cell.y, d: cell.d};
        }
        // if we cannot improve beyond precision, skip subdividing
        if (cell.max - bestCell.d <= precision) continue;
        // subdivide
        const h = cell.h / 2;
        const children = [
            {x: cell.x - h, y: cell.y - h},
            {x: cell.x + h, y: cell.y - h},
            {x: cell.x - h, y: cell.y + h},
            {x: cell.x + h, y: cell.y + h}
        ];
        for (let ch of children) {
            const d = pointToPolygonDist(ch.x, ch.y, polygon);
            const newCell = {x: ch.x, y: ch.y, h: h, d: d, max: d + h * Math.SQRT2};
            heap.push(newCell);
        }
    }

    return [bestCell.x, bestCell.y];
}

module.exports = {labelPoint: labelPoint};
